import React, { useState, useEffect } from "react";
import { CustomModal } from "../../../components/CustomModel/CustomModal";
import { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import useAxios from "../../../utils/useAxios";

const copyToClip = async (text) => {
    let copied = false;
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            copied = true;
        }
    } catch (e) {}

    if (!copied) {
        try {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed";
            textarea.style.left = "-9999px";
            textarea.style.top = "-9999px";
            textarea.setAttribute("readonly", "");
            document.body.appendChild(textarea);
            textarea.select();
            copied = document.execCommand("copy");
            document.body.removeChild(textarea);
        } catch (e) {}
    }
    return copied;
};

// Generic share modal - built for tournaments (tournamentId + tournament
// still drive the default url/text/guest-link there), but reused as-is for
// clubs/players/ratings via explicit `url` + `shareText` overrides, which
// have no guest-token endpoint of their own - that section only renders
// when tournamentId is present.
const TournamentShareModal = ({ isOpen, onClose, tournamentId, tournament, url, shareText: shareTextProp, title, linkLabel, qrCaption }) => {
    const api = useAxios();
    const [showQR, setShowQR] = useState(false);
    const [copied, setCopied] = useState(false);
    const [guestLinkCopied, setGuestLinkCopied] = useState(false);
    const [guestToken, setGuestToken] = useState(null);
    const [loadingGuestToken, setLoadingGuestToken] = useState(false);

    const publicUrl = url || `${window.location.origin}/tournaments/${tournamentId}`;
    const guestUrl = guestToken ? `${window.location.origin}/tournaments/${tournamentId}?guest_token=${guestToken}` : publicUrl;

    const shareTitle = title ?? tournament?.name;
    const shareText = shareTextProp || (shareTitle
        ? t("Join the tournament {{name}}!", { name: shareTitle })
        : t("Check out this tournament!"));
    const resolvedLinkLabel = linkLabel || t("Tournament link");
    const resolvedQrCaption = qrCaption || t("Scan with camera to open tournament");

    useEffect(() => {
        if (!isOpen) {
            setShowQR(false);
            setCopied(false);
            setGuestLinkCopied(false);
        }
    }, [isOpen]);

    const handleCopy = async (e, urlToCopy, isGuest = false) => {
        if (e) e.preventDefault();
        await copyToClip(urlToCopy);
        if (isGuest) {
            setGuestLinkCopied(true);
            setTimeout(() => setGuestLinkCopied(false), 2500);
        } else {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }
        toast.success(t("Link copied to clipboard"));
    };

    const handleGetGuestLink = async (e) => {
        if (e) e.preventDefault();
        if (guestToken) {
            handleCopy(null, `${window.location.origin}/tournaments/${tournamentId}?guest_token=${guestToken}`, true);
            return;
        }

        setLoadingGuestToken(true);
        try {
            const res = await api.post(`/api/v1.0/tournaments/${tournamentId}/guest-token/`);
            const token = res.data?.token || res.data?.key;
            if (token) {
                setGuestToken(token);
                const gUrl = `${window.location.origin}/tournaments/${tournamentId}?guest_token=${token}`;
                await copyToClip(gUrl);
                setGuestLinkCopied(true);
                setTimeout(() => setGuestLinkCopied(false), 2500);
                toast.success(t("Guest link copied to clipboard"));
            } else {
                handleCopy(null, publicUrl, true);
            }
        } catch (err) {
            console.error("Error generating guest link:", err);
            handleCopy(null, publicUrl, true);
        } finally {
            setLoadingGuestToken(false);
        }
    };

    const handleNativeShare = async (e) => {
        e.preventDefault();
        if (!navigator.share) {
            toast.warn(t("Not supported in this browser"));
            return;
        }
        try {
            await navigator.share({
                title: shareTitle || t("Tournament"),
                text: shareText,
                url: publicUrl
            });
        } catch (err) {
            if (err.name !== "AbortError") toast.error(t("Share failed"));
        }
    };

    const activeUrl = guestToken ? guestUrl : publicUrl;

    return (
        <CustomModal isOpen={isOpen} onClose={onClose}>
            <form className="modal-form">
            <div className="modal-sticky-header">
                <h2><FeatherIcon icon="share-2" /> {t("Share")}</h2>
            </div>
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "14px", boxSizing: "border-box" }}>

                {/* Link input with copy button */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{ fontSize: "12px", color: "var(--grey-blue)", fontWeight: "500" }}>
                        {resolvedLinkLabel}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <input
                            type="text"
                            readOnly
                            value={publicUrl}
                            onClick={(e) => e.target.select()}
                            style={{
                                flex: 1,
                                margin: 0,
                                padding: "8px 12px",
                                fontSize: "13px",
                                borderRadius: "8px",
                                border: "1px solid var(--white-gray)",
                                background: "var(--body-background)",
                                color: "var(--main-text-color)",
                                outline: "none"
                            }}
                        />
                        <button
                            type="button"
                            className={`btn ${copied ? "btn-success" : "btn-primary"}`}
                            onClick={(e) => handleCopy(e, publicUrl, false)}
                            style={{ margin: 0, padding: "8px 14px", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px", borderRadius: "8px" }}
                        >
                            <FeatherIcon icon={copied ? "check" : "copy"} size={15} />
                            <span>{copied ? t("Copied!") : t("Copy")}</span>
                        </button>
                    </div>
                </div>

                {/* Guest access link button - only tournaments have a guest-token endpoint */}
                {tournamentId && (
                    <div
                        className="admin-card editable"
                        onClick={handleGetGuestLink}
                        style={{
                            padding: "10px 12px",
                            gridTemplateColumns: "20px 1fr auto",
                            margin: 0,
                            cursor: loadingGuestToken ? "wait" : "pointer"
                        }}
                    >
                        <div><FeatherIcon icon="shield" size={16} /></div>
                        <div style={{ fontSize: "13px" }}>
                            <div style={{ fontWeight: "600" }}>{t("Guest Link (Direct Access)")}</div>
                            <div style={{ fontSize: "11px", color: "var(--grey-blue)" }}>{t("Allow players to view without logging in")}</div>
                        </div>
                        <div>
                            {loadingGuestToken ? (
                                <FeatherIcon icon="loader" className="spin" size={16} />
                            ) : guestLinkCopied ? (
                                <FeatherIcon icon="check" style={{ color: "var(--primary-color)" }} size={16} />
                            ) : (
                                <FeatherIcon icon="copy" size={16} />
                            )}
                        </div>
                    </div>
                )}

                {/* Social / Messenger share buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{ fontSize: "12px", color: "var(--grey-blue)", fontWeight: "500" }}>
                        {t("Send to messenger")}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                        <a
                            href={`https://t.me/share/url?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent(shareText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-card editable"
                            style={{ margin: 0, padding: "10px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", color: "#0088cc", textAlign: "center" }}
                        >
                            <FeatherIcon icon="send" size={18} />
                            <span style={{ fontSize: "12px", fontWeight: "600" }}>Telegram</span>
                        </a>

                        <a
                            href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${publicUrl}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-card editable"
                            style={{ margin: 0, padding: "10px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", color: "#25D366", textAlign: "center" }}
                        >
                            <FeatherIcon icon="message-circle" size={18} />
                            <span style={{ fontSize: "12px", fontWeight: "600" }}>WhatsApp</span>
                        </a>

                        <a
                            href={`viber://forward?text=${encodeURIComponent(`${shareText} ${publicUrl}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="admin-card editable"
                            style={{ margin: 0, padding: "10px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", color: "#7360f2", textAlign: "center" }}
                        >
                            <FeatherIcon icon="phone-call" size={18} />
                            <span style={{ fontSize: "12px", fontWeight: "600" }}>Viber</span>
                        </a>
                    </div>
                </div>

                {/* Native Web Share API if supported */}
                {navigator.share && (
                    <div
                        className="admin-card editable"
                        onClick={handleNativeShare}
                        style={{ padding: "10px 12px", gridTemplateColumns: "20px 1fr auto", margin: 0, cursor: "pointer" }}
                    >
                        <div><FeatherIcon icon="share-2" size={16} /></div>
                        <div style={{ fontSize: "13px", fontWeight: "500" }}>{t("More share options...")}</div>
                        <div><FeatherIcon icon="external-link" size={14} /></div>
                    </div>
                )}

                {/* QR Code toggle */}
                <div
                    className="admin-card editable"
                    onClick={(e) => { e.preventDefault(); setShowQR(p => !p); }}
                    style={{ padding: "10px 12px", gridTemplateColumns: "20px 1fr auto", margin: 0, cursor: "pointer" }}
                >
                    <div><FeatherIcon icon="grid" size={16} /></div>
                    <div style={{ fontSize: "13px", fontWeight: "500" }}>{t("QR Code")}</div>
                    <div><FeatherIcon icon={showQR ? "chevron-up" : "chevron-down"} size={14} /></div>
                </div>

                {showQR && (
                    <div style={{ textAlign: "center", padding: "12px", background: "#ffffff", borderRadius: "10px", border: "1px solid var(--white-gray)" }}>
                        <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(activeUrl)}`}
                            alt="QR Code"
                            style={{ borderRadius: "8px", maxWidth: "200px", height: "auto" }}
                        />
                        <div style={{ fontSize: "11px", color: "var(--grey-blue)", marginTop: "6px" }}>
                            {resolvedQrCaption}
                        </div>
                    </div>
                )}
            </div>
            </form>
        </CustomModal>
    );
};

export default TournamentShareModal;
